import { CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { ToolCall } from '@/types/ai-assistant.types';

interface AIAssistantToolCardProps {
  readonly tool: ToolCall;
}

export function AIAssistantToolCard({ tool }: Readonly<AIAssistantToolCardProps>) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusIcon = () => {
    switch (tool.status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'running':
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (tool.status) {
      case 'success':
        return 'Completed';
      case 'error':
        return 'Failed';
      case 'running':
        return 'Running...';
      case 'pending':
        return 'Pending...';
      default:
        return '';
    }
  };

  return (
    <div className={cn(
      'rounded-lg border bg-card p-3 space-y-2',
      tool.status === 'error' && 'border-destructive/50'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {getStatusIcon()}
          <span className="text-sm font-medium truncate">{tool.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{getStatusText()}</span>
          {(tool.result !== undefined || tool.error !== undefined) && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {tool.status === 'running' && tool.progress !== undefined && (
        <Progress value={tool.progress} className="h-1" />
      )}

      {/* Error Message */}
      {tool.status === 'error' && tool.error && (
        <div className="text-xs text-destructive bg-destructive/10 rounded p-2">
          {tool.error}
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && tool.result !== undefined && (
        <div className="text-xs bg-muted rounded p-2 max-h-48 overflow-auto">
          <pre className="whitespace-pre-wrap break-words">
            {typeof tool.result === 'string' 
              ? tool.result 
              : JSON.stringify(tool.result, null, 2)
            }
          </pre>
        </div>
      )}

      {/* Action Buttons */}
      {tool.status === 'success' && tool.result !== undefined && (
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" className="h-7 text-xs">
            View Details
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs">
            Export
          </Button>
        </div>
      )}
    </div>
  );
}
